import {
  Drawer,
  type DrawerBackdropProps,
  type DrawerCloseProps,
  type DrawerContentProps,
  type DrawerDescriptionProps,
  type DrawerPopupProps,
  type DrawerTitleProps,
  type DrawerViewportProps,
  mergeProps,
} from '@base-ui/react';
import type { ReactNode } from 'react';
import { IoClose, IoCloseOutline } from 'react-icons/io5';
import { FuuzeButton } from './fuuze-button.js';
import styles from './fuuze-drawer.module.css';

function Backdrop(props: DrawerBackdropProps) {
  const mergedProps = mergeProps<typeof Drawer.Backdrop>(props, { className: styles.backdrop });
  return <Drawer.Backdrop {...mergedProps} />;
}

function Close(props: DrawerCloseProps) {
  const mergedProps = mergeProps<typeof Drawer.Close>(props, { className: styles.close });
  return (
    <Drawer.Close
      render={(props, state) => (
        <FuuzeButton {...props} icon style={{ ...props.style }}>
          <IoClose size={32} />
        </FuuzeButton>
      )}
      {...mergedProps}
    ></Drawer.Close>
  );
}

function Content(props: DrawerContentProps) {
  const mergedProps = mergeProps<typeof Drawer.Content>(props, { className: styles.content });
  return <Drawer.Content {...mergedProps} />;
}

function Description(props: DrawerDescriptionProps) {
  const mergedProps = mergeProps<typeof Drawer.Description>(props, { className: styles.description });
  return <Drawer.Description {...mergedProps} />;
}

function Popup(props: DrawerPopupProps) {
  const mergedProps = mergeProps<typeof Drawer.Popup>(props, { className: styles.popup });
  return <Drawer.Popup {...mergedProps} />;
}

function Title(props: DrawerTitleProps) {
  const mergedProps = mergeProps<typeof Drawer.Title>(props, { className: styles.title });
  return <Drawer.Title {...mergedProps} />;
}

function Viewport(props: DrawerViewportProps) {
  const mergedProps = mergeProps<typeof Drawer.Viewport>(props, { className: styles.viewport });
  return <Drawer.Viewport {...mergedProps} />;
}

export const FuuzeDrawer = {
  Backdrop,
  Close,
  Content,
  Description,
  Indent: Drawer.Indent,
  IndentBackground: Drawer.IndentBackground,
  Portal: Drawer.Portal,
  Popup,
  Root: Drawer.Root,
  SwipeArea: Drawer.SwipeArea,
  Title,
  Trigger: Drawer.Trigger,
  Viewport,
};

export type FuuzeDrawerProps = {
  children?: ReactNode;
  closeIcon?: boolean;
  description?: string;
  title?: string;
  RootProps?: Omit<Drawer.Root.Props, 'children'> & { children?: ReactNode };
  ContentProps?: DrawerContentProps;
};

export function FuuzeDrawerComponent({
  children,
  closeIcon = true,
  description,
  title,
  RootProps,
  ContentProps,
}: FuuzeDrawerProps) {
  return (
    <FuuzeDrawer.Root swipeDirection='down' {...RootProps}>
      <FuuzeDrawer.Portal>
        <Backdrop />
        <Viewport>
          <Popup>
            <div className={styles.handle} />
            <Content {...ContentProps}>
              {title && <Title>{title}</Title>}
              {description && <Description>{description}</Description>}
              {children}
              {closeIcon && (
                <div className={styles.closeWrapper}>
                  <Close aria-label='Close'>
                    <IoCloseOutline />
                  </Close>
                </div>
              )}
            </Content>
          </Popup>
        </Viewport>
      </FuuzeDrawer.Portal>
    </FuuzeDrawer.Root>
  );
}
